import { Link } from 'react-router-dom';
import type { Page } from '@connaro/shared';
import { api } from '../api/client';
import { ResourceList } from '../components/ResourceList';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

const fmt = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString() : '—';

interface Org {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}
interface Site {
  id: string;
  name: string;
  slug: string;
  organizationId: string;
  createdAt: string;
}
interface Device {
  id: string;
  name: string;
  status: string;
  platform: string | null;
  siteId: string;
  createdAt: string;
}
interface Fabric {
  id: string;
  name: string;
  type: string;
  createdAt: string;
}
interface Alert {
  id: string;
  severity: string;
  status: string;
  title: string;
  source: string;
  createdAt: string;
}
interface Audit {
  id: string;
  action: string;
  resourceType: string;
  outcome: string;
  createdAt: string;
}

export function OrganizationsPage(): JSX.Element {
  const t = useT();
  return (
    <ResourceList<Org>
      title={t('pages.organizations.title')}
      load={() => api.get<Page<Org>>('/organizations?limit=50')}
      columns={[
        { header: t('nav.organizations'), render: (r) => r.name },
        { header: 'Slug', render: (r) => r.slug },
        { header: 'Created', render: (r) => fmt(r.createdAt) },
      ]}
      create={{
        label: t('common.create'),
        fields: [
          { name: 'slug', label: 'Slug', required: true, placeholder: 'hq' },
          { name: 'name', label: t('nav.organizations'), required: true },
        ],
        submit: (v) => api.post('/organizations', v).then(() => undefined),
      }}
    />
  );
}

export function SitesPage(): JSX.Element {
  const t = useT();
  const orgs = useAsync(() => api.get<Page<Org>>('/organizations?limit=100'), []);
  const options = (orgs.data?.items ?? []).map((o) => ({ value: o.id, label: o.name }));
  return (
    <ResourceList<Site>
      title={t('pages.sites.title')}
      load={() => api.get<Page<Site>>('/sites?limit=50')}
      loadKeys={[options.length]}
      columns={[
        { header: t('nav.sites'), render: (r) => r.name },
        { header: 'Slug', render: (r) => r.slug },
        { header: 'Created', render: (r) => fmt(r.createdAt) },
      ]}
      create={{
        label: t('common.create'),
        fields: [
          {
            name: 'organizationId',
            label: t('nav.organizations'),
            type: 'select',
            required: true,
            options,
          },
          { name: 'slug', label: 'Slug', required: true, placeholder: 'sju-01' },
          { name: 'name', label: t('nav.sites'), required: true },
        ],
        submit: (v) => api.post('/sites', v).then(() => undefined),
      }}
    />
  );
}

export function DevicesPage(): JSX.Element {
  const t = useT();
  const sites = useAsync(() => api.get<Page<Site>>('/sites?limit=100'), []);
  const options = (sites.data?.items ?? []).map((s) => ({ value: s.id, label: s.name }));
  return (
    <ResourceList<Device>
      title={t('pages.devices.title')}
      load={() => api.get<Page<Device>>('/edge-devices?limit=50')}
      loadKeys={[options.length]}
      columns={[
        { header: t('nav.devices'), render: (r) => <Link to={`/devices/${r.id}`}>{r.name}</Link> },
        {
          header: 'Status',
          render: (r) => <span className={`chip chip--${r.status.toLowerCase()}`}>{r.status}</span>,
        },
        { header: t('device.platform'), render: (r) => r.platform ?? '—' },
        { header: 'Created', render: (r) => fmt(r.createdAt) },
      ]}
      create={{
        label: t('common.create'),
        fields: [
          { name: 'siteId', label: t('nav.sites'), type: 'select', required: true, options },
          { name: 'name', label: t('nav.devices'), required: true, placeholder: 'edge-1' },
        ],
        submit: (v) => api.post('/edge-devices', v).then(() => undefined),
      }}
      rowActions={(row, reload) =>
        row.status === 'ENROLLING' ? (
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => void api.post(`/edge-devices/${row.id}/approve`).then(reload)}
            >
              {t('device.approve')}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--danger"
              onClick={() => void api.post(`/edge-devices/${row.id}/reject`, {}).then(reload)}
            >
              {t('device.reject')}
            </button>
          </>
        ) : null
      }
    />
  );
}

export function FabricsPage(): JSX.Element {
  const t = useT();
  return (
    <ResourceList<Fabric>
      title={t('pages.fabrics.title')}
      load={() => api.get<Page<Fabric>>('/fabrics?limit=50')}
      columns={[
        { header: t('nav.fabrics'), render: (r) => r.name },
        { header: 'Type', render: (r) => r.type },
        { header: 'Created', render: (r) => fmt(r.createdAt) },
      ]}
      create={{
        label: t('common.create'),
        fields: [
          { name: 'name', label: t('nav.fabrics'), required: true },
          {
            name: 'type',
            label: 'Type',
            type: 'select',
            options: [
              { value: 'HUB_SPOKE', label: 'Hub & Spoke' },
              { value: 'FULL_MESH', label: 'Full Mesh' },
              { value: 'PARTIAL_MESH', label: 'Partial Mesh' },
            ],
          },
        ],
        submit: (v) => api.post('/fabrics', v).then(() => undefined),
      }}
    />
  );
}

export function AlertsPage(): JSX.Element {
  const t = useT();
  return (
    <ResourceList<Alert>
      title={t('pages.alerts.title')}
      load={() => api.get<Page<Alert>>('/alerts?limit=50')}
      columns={[
        {
          header: 'Severity',
          render: (r) => (
            <span className={`chip chip--${r.severity.toLowerCase()}`}>{r.severity}</span>
          ),
        },
        { header: 'Title', render: (r) => r.title },
        { header: 'Status', render: (r) => r.status },
        { header: 'Created', render: (r) => fmt(r.createdAt) },
      ]}
      rowActions={(row, reload) =>
        row.status === 'RESOLVED' ? null : (
          <>
            {row.status === 'OPEN' ? (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void api.post(`/alerts/${row.id}/acknowledge`).then(reload)}
              >
                Ack
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => void api.post(`/alerts/${row.id}/resolve`).then(reload)}
            >
              Resolve
            </button>
          </>
        )
      }
    />
  );
}

export function AuditPage(): JSX.Element {
  const t = useT();
  return (
    <ResourceList<Audit>
      title={t('pages.audit.title')}
      load={() => api.get<Page<Audit>>('/audit-events?limit=50')}
      columns={[
        { header: 'Action', render: (r) => r.action },
        { header: 'Resource', render: (r) => r.resourceType },
        {
          header: 'Outcome',
          render: (r) => (
            <span className={`chip chip--${r.outcome.toLowerCase()}`}>{r.outcome}</span>
          ),
        },
        { header: 'When', render: (r) => fmt(r.createdAt) },
      ]}
    />
  );
}
